import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  HelpOutline as HelpOutlineIcon,
  CloudUpload as CloudUploadIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { ComplianceStep, workflowService } from '../services/workflowService';
import { pdfGenerator, FormFieldData } from '../services/pdfGenerator';
import { formProgressService } from '../services/formProgressService';

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'textarea' | 'file';
  required?: boolean;
  helpText?: string;
  options?: string[];
  value?: any;
  section?: string;
}

interface GuidedFormFillerProps {
  open: boolean;
  onClose: () => void;
  step: ComplianceStep;
  workflowData?: {
    name?: string;
    jurisdiction?: string;
    device_class?: string;
    device_type?: string;
    device_category?: string;
  };
  onSave?: (filledFormData: any, filledPdfUrl?: string) => void;
}

const GuidedFormFiller: React.FC<GuidedFormFillerProps> = ({
  open,
  onClose,
  step,
  workflowData,
  onSave,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: 'success' | 'error' | 'info' }>({ open: false, message: '' });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Get form code - try multiple sources
  const formCode = step.configuration?.form_code || 
                   step.required_forms?.[0] || 
                   (step.title.toLowerCase().includes('md-3') ? 'MD-3' :
                    step.title.toLowerCase().includes('md-4') ? 'MD-4' :
                    step.title.toLowerCase().includes('md-14') ? 'MD-14' :
                    step.title.toLowerCase().includes('md-22') ? 'MD-22' :
                    step.title.toLowerCase().includes('md-7') ? 'MD-7' :
                    step.title.toLowerCase().includes('technical file') ? 'TECHNICAL_FILE' :
                    step.title.toLowerCase().includes('dmf') ? 'DMF' :
                    '');
  
  // Get form URL from step configuration
  const formUrl = step.configuration?.form_download_url || 
                  step.configuration?.form_downloads?.find((f: any) => 
                    (f.form_code || f.form_name || '').toUpperCase() === formCode.toUpperCase()
                  )?.form_download_url ||
                  step.configuration?.form_downloads?.[0]?.form_download_url;
  
  const formName = step.configuration?.form_name || 
                   step.configuration?.form_code || 
                   formCode || 
                   step.title || 
                   'Form';
  
  const checklistUrl = step.configuration?.checklist_download_url ||
                      step.configuration?.form_downloads?.find((f: any) => 
                        (f.form_code || f.form_name || '').toUpperCase() === formCode.toUpperCase()
                      )?.checklist_download_url ||
                      step.configuration?.form_downloads?.[0]?.checklist_download_url;

  // Define form sections and fields based on form type
  // This is a simplified version - in production, you'd extract this from the PDF or have form templates
  const formSections = getFormSections(formCode, workflowData);
  
  // Debug logging (remove in production)
  useEffect(() => {
    if (open) {
      console.log('GuidedFormFiller - Form Detection:', {
        formCode,
        formName,
        stepTitle: step.title,
        requiredForms: step.required_forms,
        configFormCode: step.configuration?.form_code,
        formSectionsCount: formSections.length
      });
    }
  }, [open, formCode, formName, step.title, step.required_forms, step.configuration?.form_code, formSections.length]);

  // Load saved progress on mount
  useEffect(() => {
    if (open && step.id) {
      const savedProgress = formProgressService.loadProgress(step.id, formCode);
      
      if (savedProgress && savedProgress.formData) {
        setFormData(savedProgress.formData);
        if (savedProgress.currentSection !== undefined) {
          setActiveStep(savedProgress.currentSection);
        }
        setSnackbar({ open: true, message: 'Restored previous progress', severity: 'info' });
      } else if (workflowData) {
        // Auto-fill form data from workflow context if no saved progress
        const autoFillData: Record<string, any> = {
          company_name: workflowData.name || '',
          jurisdiction: workflowData.jurisdiction || '',
          device_class: workflowData.device_class || '',
          device_type: workflowData.device_type || '',
          device_category: workflowData.device_category || '',
          application_date: new Date().toISOString().split('T')[0],
        };
        setFormData(autoFillData);
      }
    }
  }, [open, step.id, formCode, workflowData]);

  // Auto-save progress when form data changes
  useEffect(() => {
    if (open && step.id && Object.keys(formData).length > 0) {
      const autoSaveTimeout = setTimeout(() => {
        formProgressService.saveProgress(step.id, formCode, formData, activeStep);
        setHasUnsavedChanges(false);
      }, 2000); // Auto-save after 2 seconds of inactivity

      return () => clearTimeout(autoSaveTimeout);
    }
  }, [formData, activeStep, open, step.id, formCode]);

  // Load PDF preview
  useEffect(() => {
    if (formUrl && open) {
      setPdfPreviewUrl(formUrl);
    }
  }, [formUrl, open]);

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value,
    }));
    setHasUnsavedChanges(true);
  };

  const handleNext = () => {
    if (activeStep < formSections.length - 1) {
      setActiveStep(prev => {
        const newStep = prev + 1;
        // Save progress when moving to next step
        if (step.id) {
          const formCode = step.configuration?.form_code || '';
          formProgressService.saveProgress(step.id, formCode, formData, newStep);
        }
        return newStep;
      });
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => {
        const newStep = prev - 1;
        // Save progress when going back
        if (step.id) {
          const formCode = step.configuration?.form_code || '';
          formProgressService.saveProgress(step.id, formCode, formData, newStep);
        }
        return newStep;
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Generate filled PDF from formData
      setGeneratingPdf(true);
      const pdfBlob = await pdfGenerator.generateFilledPDF({
        formData: formData as FormFieldData,
        formName: formName,
        templateUrl: formUrl,
      });
      setGeneratingPdf(false);

      // 2. Upload PDF to step attachments
      setUploading(true);
      const pdfFile = new File([pdfBlob], `${formCode}_filled_${Date.now()}.pdf`, { type: 'application/pdf' });
      const uploadResult = await workflowService.uploadStepAttachment(step.id, pdfFile);
      setUploading(false);

      // 3. Clear saved progress after successful save
      formProgressService.clearProgress(step.id, formCode);

      // 4. Call onSave callback with form data and PDF URL
      if (onSave) {
        await onSave(formData, uploadResult.file_url);
      }

      setSnackbar({ open: true, message: 'Form saved and PDF uploaded successfully!', severity: 'success' });
      
      // Close dialog after save
      setTimeout(() => {
        setSaving(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving form:', error);
      setSaving(false);
      setGeneratingPdf(false);
      setUploading(false);
      setSnackbar({ 
        open: true, 
        message: error instanceof Error ? error.message : 'Failed to save form. Please try again.', 
        severity: 'error' 
      });
    }
  };

  const handleExport = async () => {
    try {
      setGeneratingPdf(true);
      const pdfBlob = await pdfGenerator.generateFilledPDF({
        formData: formData as FormFieldData,
        formName: formName,
        templateUrl: formUrl,
      });
      
      const filename = `${formName.replace(/\s+/g, '_')}_filled_${Date.now()}.pdf`;
      pdfGenerator.downloadPDF(pdfBlob, filename);
      
      setSnackbar({ open: true, message: 'PDF exported successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setSnackbar({ 
        open: true, 
        message: error instanceof Error ? error.message : 'Failed to export PDF. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleRestoreProgress = () => {
    if (step.id) {
      const formCode = step.configuration?.form_code || '';
      const savedProgress = formProgressService.loadProgress(step.id, formCode);
      
      if (savedProgress) {
        setFormData(savedProgress.formData);
        if (savedProgress.currentSection !== undefined) {
          setActiveStep(savedProgress.currentSection);
        }
        setSnackbar({ open: true, message: 'Progress restored', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'No saved progress found', severity: 'info' });
      }
    }
  };

  const currentSection = formSections[activeStep];
  const isLastStep = activeStep === formSections.length - 1;
  const isFirstStep = activeStep === 0;

  // Check if current section is complete
  const isSectionComplete = currentSection?.fields.every(field => {
    if (field.required) {
      return formData[field.id] !== undefined && formData[field.id] !== '';
    }
    return true;
  }) || false;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            <Typography variant="h6">
              Fill {formName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Left Side: Form Wizard */}
          <Box sx={{ flex: 1, p: 3, overflowY: 'auto', borderRight: '1px solid', borderColor: 'divider' }}>
            <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 3 }}>
              {formSections.map((section, index) => (
                <Step key={section.id} completed={index < activeStep}>
                  <StepLabel>
                    <Typography variant="subtitle2">{section.title}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {currentSection && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {currentSection.title}
                </Typography>
                {currentSection.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {currentSection.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {currentSection.fields.map((field) => (
                    <Box key={field.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {field.label}
                          {field.required && <span style={{ color: 'red' }}> *</span>}
                        </Typography>
                        {field.helpText && (
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            <HelpOutlineIcon fontSize="small" color="action" />
                          </IconButton>
                        )}
                      </Box>
                      {field.helpText && (
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          {field.helpText}
                        </Typography>
                      )}
                      {renderField(field, formData[field.id], (value) => handleFieldChange(field.id, value))}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Checklist Integration */}
            {checklistUrl && (
              <Accordion sx={{ mt: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">View Checklist</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DescriptionIcon />}
                    onClick={() => window.open(checklistUrl, '_blank')}
                  >
                    Open Checklist
                  </Button>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>

          {/* Right Side: PDF Preview */}
          <Box sx={{ width: '40%', borderLeft: '1px solid', borderColor: 'divider', p: 2, overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2">Form Preview</Typography>
              <IconButton
                size="small"
                onClick={() => setShowPdfPreview(!showPdfPreview)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Box>
            
            {pdfPreviewUrl && showPdfPreview ? (
              <iframe
                src={pdfPreviewUrl}
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="PDF Preview"
              />
            ) : (
              <Card>
                <CardContent>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Click the eye icon to view the original form PDF
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => window.open(formUrl, '_blank')}
                    >
                      Download Form
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {isSectionComplete && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Section Complete"
                color="success"
                size="small"
              />
            )}
            {hasUnsavedChanges && (
              <Chip
                label="Unsaved changes"
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
            {formProgressService.hasProgress(step.id, formCode) && (
              <Button
                size="small"
                startIcon={<RestoreIcon />}
                onClick={handleRestoreProgress}
                variant="outlined"
              >
                Restore Progress
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose}>
              Cancel
            </Button>
            {!isFirstStep && (
              <Button onClick={handleBack}>
                Back
              </Button>
            )}
            {!isLastStep ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isSectionComplete}
              >
                Next
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={generatingPdf ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={handleExport}
                  disabled={generatingPdf}
                >
                  {generatingPdf ? 'Generating...' : 'Export PDF'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    saving || generatingPdf || uploading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                  onClick={handleSave}
                  disabled={saving || generatingPdf || uploading || !isSectionComplete}
                >
                  {saving || generatingPdf || uploading 
                    ? (generatingPdf ? 'Generating PDF...' : uploading ? 'Uploading...' : 'Saving...') 
                    : 'Save & Upload PDF'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ open: false, message: '' })} 
          severity={snackbar.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

// Helper function to render form fields
function renderField(field: FormField, value: any, onChange: (value: any) => void) {
  switch (field.type) {
    case 'text':
    case 'number':
    case 'date':
      return (
        <TextField
          fullWidth
          type={field.type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          size="small"
          variant="outlined"
        />
      );
    case 'textarea':
      return (
        <TextField
          fullWidth
          multiline
          rows={4}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          size="small"
          variant="outlined"
        />
      );
    case 'select':
      return (
        <FormControl fullWidth size="small" required={field.required}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label={field.label}
          >
            {field.options?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case 'file':
      return (
        <Button
          variant="outlined"
          component="label"
          size="small"
          fullWidth
        >
          Upload File
          <input
            type="file"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onChange(file.name);
            }}
          />
        </Button>
      );
    default:
      return null;
  }
}

// Helper function to get form sections based on form code
function getFormSections(formCode: string, workflowData?: any): Array<{
  id: string;
  title: string;
  description?: string;
  fields: FormField[];
}> {
  const code = formCode.toUpperCase();
  
  // MD-3 Form (Manufacturing License - India)
  if (code.includes('MD-3') || code.includes('MD3')) {
    return [
      {
        id: 'applicant_info',
        title: 'Applicant Information',
        description: 'Enter details about the applicant company',
        fields: [
          {
            id: 'company_name',
            label: 'Company Name',
            type: 'text',
            required: true,
            helpText: 'Full legal name of the manufacturing company',
          },
          {
            id: 'address',
            label: 'Registered Address',
            type: 'textarea',
            required: true,
          },
          {
            id: 'contact_person',
            label: 'Contact Person',
            type: 'text',
            required: true,
          },
          {
            id: 'email',
            label: 'Email Address',
            type: 'text',
            required: true,
          },
          {
            id: 'phone',
            label: 'Phone Number',
            type: 'text',
            required: true,
          },
        ],
      },
      {
        id: 'manufacturing_details',
        title: 'Manufacturing Details',
        description: 'Details about the manufacturing facility and devices',
        fields: [
          {
            id: 'manufacturing_site',
            label: 'Manufacturing Site Address',
            type: 'textarea',
            required: true,
          },
          {
            id: 'device_class',
            label: 'Device Class',
            type: 'select',
            required: true,
            options: ['Class A', 'Class B', 'Class C', 'Class D'],
            helpText: 'Classification of the medical device',
          },
          {
            id: 'device_type',
            label: 'Device Type',
            type: 'text',
            required: true,
          },
          {
            id: 'gmp_certificate',
            label: 'GMP Certificate',
            type: 'file',
            required: true,
            helpText: 'Upload GMP certificate if available',
          },
        ],
      },
      {
        id: 'declaration',
        title: 'Declaration',
        description: 'Confirm compliance and submit',
        fields: [
          {
            id: 'compliance_declaration',
            label: 'I declare compliance with Medical Devices Rules, 2017',
            type: 'select',
            required: true,
            options: ['Yes', 'No'],
          },
          {
            id: 'application_date',
            label: 'Application Date',
            type: 'date',
            required: true,
          },
        ],
      },
    ];
  }

  // MD-4 Form (Loan License - India)
  if (code.includes('MD-4') || code.includes('MD4')) {
    return [
      {
        id: 'applicant_info',
        title: 'Applicant Information',
        fields: [
          { id: 'company_name', label: 'Company Name', type: 'text', required: true },
          { id: 'address', label: 'Address', type: 'textarea', required: true },
          { id: 'contact_person', label: 'Contact Person', type: 'text', required: true },
          { id: 'email', label: 'Email Address', type: 'text', required: true },
          { id: 'phone', label: 'Phone Number', type: 'text', required: true },
        ],
      },
      {
        id: 'loan_license_details',
        title: 'Loan License Details',
        fields: [
          { id: 'loan_license_type', label: 'License Type', type: 'select', required: true, options: ['Class A', 'Class B'] },
          { id: 'manufacturing_site', label: 'Manufacturing Site', type: 'textarea', required: true },
          { id: 'loan_period', label: 'Loan Period', type: 'text', required: true, helpText: 'Duration of the loan license' },
        ],
      },
      {
        id: 'declaration',
        title: 'Declaration',
        fields: [
          { id: 'compliance_declaration', label: 'I declare compliance with Medical Devices Rules, 2017', type: 'select', required: true, options: ['Yes', 'No'] },
          { id: 'application_date', label: 'Application Date', type: 'date', required: true },
        ],
      },
    ];
  }

  // Technical File (EU MDR)
  if (code.includes('TECHNICAL') || code.includes('TECHNICAL_FILE') || code.includes('TECH FILE')) {
    return [
      {
        id: 'device_description',
        title: 'Device Description',
        description: 'Provide detailed information about the medical device',
        fields: [
          { id: 'device_name', label: 'Device Name', type: 'text', required: true },
          { id: 'device_class', label: 'Device Classification', type: 'select', required: true, options: ['Class I', 'Class IIa', 'Class IIb', 'Class III'] },
          { id: 'intended_purpose', label: 'Intended Purpose', type: 'textarea', required: true },
          { id: 'device_description', label: 'Device Description', type: 'textarea', required: true },
          { id: 'indications_for_use', label: 'Indications for Use', type: 'textarea', required: true },
        ],
      },
      {
        id: 'design_manufacturing',
        title: 'Design and Manufacturing Information',
        fields: [
          { id: 'design_specifications', label: 'Design Specifications', type: 'textarea', required: true },
          { id: 'manufacturing_process', label: 'Manufacturing Process', type: 'textarea', required: true },
          { id: 'sterilization_method', label: 'Sterilization Method (if applicable)', type: 'text', required: false },
        ],
      },
      {
        id: 'safety_performance',
        title: 'Safety and Performance',
        fields: [
          { id: 'risk_analysis', label: 'Risk Analysis Summary', type: 'textarea', required: true },
          { id: 'clinical_evaluation', label: 'Clinical Evaluation Summary', type: 'textarea', required: true },
          { id: 'biocompatibility', label: 'Biocompatibility Assessment', type: 'textarea', required: false },
        ],
      },
    ];
  }

  // Device Master File (DMF)
  if (code.includes('DMF') || code.includes('DEVICE MASTER')) {
    return [
      {
        id: 'executive_summary',
        title: 'Executive Summary',
        fields: [
          { id: 'device_name', label: 'Device Name', type: 'text', required: true },
          { id: 'device_class', label: 'Device Class', type: 'select', required: true, options: ['Class A', 'Class B', 'Class C', 'Class D'] },
          { id: 'summary', label: 'Executive Summary', type: 'textarea', required: true, helpText: 'Brief overview of the device and its intended use' },
        ],
      },
      {
        id: 'device_description',
        title: 'Device Description',
        fields: [
          { id: 'product_specifications', label: 'Product Specifications', type: 'textarea', required: true },
          { id: 'labelling_information', label: 'Labelling Information', type: 'textarea', required: true },
          { id: 'packaging_details', label: 'Packaging Details', type: 'textarea', required: true },
        ],
      },
      {
        id: 'risk_analysis',
        title: 'Risk Analysis',
        fields: [
          { id: 'risk_management_summary', label: 'Risk Management Summary', type: 'textarea', required: true },
          { id: 'hazards_identified', label: 'Hazards Identified', type: 'textarea', required: true },
          { id: 'mitigation_measures', label: 'Mitigation Measures', type: 'textarea', required: true },
        ],
      },
    ];
  }

  // Generic form structure for other forms
  return [
    {
      id: 'basic_info',
      title: 'Basic Information',
      fields: [
        { id: 'company_name', label: 'Company Name', type: 'text', required: true },
        { id: 'address', label: 'Address', type: 'textarea', required: true },
        { id: 'contact_email', label: 'Email', type: 'text', required: true },
        { id: 'contact_phone', label: 'Phone', type: 'text', required: true },
      ],
    },
    {
      id: 'device_info',
      title: 'Device Information',
      fields: [
        { id: 'device_name', label: 'Device Name', type: 'text', required: true },
        { id: 'device_class', label: 'Device Class', type: 'select', required: true, options: ['Class A', 'Class B', 'Class C', 'Class D'] },
        { id: 'device_description', label: 'Description', type: 'textarea', required: false },
      ],
    },
    {
      id: 'submission',
      title: 'Submission',
      fields: [
        { id: 'application_date', label: 'Application Date', type: 'date', required: true },
        { id: 'signature', label: 'Digital Signature Required', type: 'text', required: true, helpText: 'To be signed digitally' },
      ],
    },
  ];
}

export default GuidedFormFiller;

